/**
 * Rollout — is the Configurable Product Builder switched on for THIS member of
 * THIS company, as resolved by the server?
 *
 * Spec: docs/configurable-product-builder/adr/ADR-007-rollout-controls.md §5
 * (the app gets `hasRollout` for UX only), PR0-SCOPE.md "App". The server's
 * `GET /cpb/capabilities` (`fetchCpbCapabilities` in queries/cpb.js) answers
 *
 *   { author, publish, serve, ai,          // booleans — rollout ∧ entitlements ∧ RBAC
 *     entitled: { core, ai },
 *     permissions: { 'create-edit-products', 'publish-products' },
 *     reasons: { author, publish, serve, ai } }
 *
 * and this file only READS that object. The answer is per MEMBER (it includes
 * their role's permissions), so it is passed in as its own object rather than
 * read off the company payload.
 *
 * Rollout is NOT an entitlement: an unreleased builder is not something to
 * buy, so nothing here — or in the 403 handling — shows upsell copy.
 *
 * Fail closed: no capabilities yet (loading, a failed request, an older
 * server), a malformed object, or an unknown flag all read as off. Client
 * gating is UX; the server enforces every one of these. Pure, no React, no Redux.
 */

/** The flags, by their short and public (`cpb.<name>`) names. `quote-freeze` (CPB PR 9c)
 * is a read flag for order screens: an expired snapshot-bound quote is price-frozen. `ai-v2`
 * (Draft with AI v2) needs `ai`: the upload's copy says what the AI will set up. `archive`
 * (CPB PR 4b, ATTR-077): archive instead of delete — the builder's "Delete … from your
 * catalog" and Restore. */
// `ai-import`: AI catalog import — a whole catalog, one draft per product (a variant of ai-v2).
export const ROLLOUT_FLAGS = Object.freeze(['author', 'publish', 'serve', 'ai', 'ai-v2', 'ai-import', 'choice-display', 'quote-freeze', 'archive']);

/** `error.name` / `error.details.reason` on the server's coded 403. */
export const ROLLOUT_NOT_ENABLED_NAME = 'RolloutNotEnabled';
export const ROLLOUT_NOT_ENABLED_REASON = 'rollout_not_enabled';

/** Every flag off: what a caller reads before (or instead of) a real answer. */
export const CLOSED_CAPABILITIES = Object.freeze({
  author: false,
  publish: false,
  serve: false,
  ai: false,
  'ai-v2': false,
  'ai-import': false,
  'choice-display': false,
  'quote-freeze': false,
  archive: false,
  entitled: Object.freeze({ core: false, ai: false }),
  permissions: Object.freeze({ 'create-edit-products': false, 'publish-products': false }),
  reasons: Object.freeze({ author: 'unknown', publish: 'unknown', serve: 'unknown', ai: 'unknown', 'ai-v2': 'unknown', 'ai-import': 'unknown', 'choice-display': 'unknown', 'quote-freeze': 'unknown', archive: 'unknown' }),
});

/** `cpb.author` | `author` → `author`; anything else → null. */
export const normalizeRolloutFlag = (flag) => {
  if (typeof flag !== 'string') return null;
  const v = flag.trim().toLowerCase();
  const short = v.startsWith('cpb.') ? v.slice(4) : v;
  return ROLLOUT_FLAGS.includes(short) ? short : null;
};

const isObject = (v) => v !== null && typeof v === 'object' && !Array.isArray(v);

/**
 * A capabilities response reduced to its known shape; anything that is not
 * exactly `true` reads as false. Never throws.
 */
export const readCapabilities = (raw) => {
  if (!isObject(raw)) return CLOSED_CAPABILITIES;
  const out = { reasons: {} };
  for (const flag of ROLLOUT_FLAGS) {
    out[flag] = raw[flag] === true;
    const reason = isObject(raw.reasons) ? raw.reasons[flag] : null;
    out.reasons[flag] = typeof reason === 'string' ? reason : 'unknown';
  }
  const entitled = isObject(raw.entitled) ? raw.entitled : {};
  out.entitled = { core: entitled.core === true, ai: entitled.ai === true };
  const perms = isObject(raw.permissions) ? raw.permissions : {};
  out.permissions = {
    'create-edit-products': perms['create-edit-products'] === true,
    'publish-products': perms['publish-products'] === true,
  };
  return out;
};

/**
 * Is `flag` ('author' | 'publish' | 'serve' | 'ai', or 'cpb.<name>') on for
 * this member? UX only; the server enforces. Fails closed.
 */
export const hasRollout = (capabilities, flag) => {
  const short = normalizeRolloutFlag(flag);
  if (!short) return false;
  return readCapabilities(capabilities)[short] === true;
};

export const errorCandidates = (error) => [
  error?.response?.data?.error,
  error?.response?.data,
  error?.error,
  error,
];

/**
 * `{ flag }` when an API error is the server's coded rollout 403, else null.
 * Accepts an axios error, the Strapi SDK's rethrown `response.data`, a parsed
 * fetch body, or the `error` object inside it — like `readModuleNotAvailable`.
 */
export const readRolloutNotEnabled = (error) => {
  for (const candidate of errorCandidates(error)) {
    const details = candidate?.details;
    if (details && details.reason === ROLLOUT_NOT_ENABLED_REASON) {
      const short = normalizeRolloutFlag(details.flag);
      return { flag: short ? `cpb.${short}` : null };
    }
  }
  return null;
};

/** `error.name` on the server's coded 423 / 409 (PR1-SCOPE.md §4, §5). */
export const PRODUCT_IN_REVISION_MODE_NAME = 'ProductInRevisionMode';
export const DRAFT_VERSION_CONFLICT_NAME = 'DraftVersionConflict';

/**
 * `{ productId, draftId }` when an API error is the server's 423 legacy-write
 * lock on an enrolled product (PR1-SCOPE.md §5 ADR-006), else null. Same
 * error-shape tolerance as `readRolloutNotEnabled`: an axios error, the
 * Strapi SDK's rethrown `response.data`, a parsed fetch body, or the `error`
 * object inside it. The interceptor in queries/strapi-client.js does NOT
 * rewrite a 423 (it only special-cases 403), so the server's own message is
 * always on the error already — this reader is for a caller that wants the
 * ids, not the message.
 */
export const readProductInRevisionMode = (error) => {
  for (const candidate of errorCandidates(error)) {
    if (candidate?.name === PRODUCT_IN_REVISION_MODE_NAME) {
      const details = candidate?.details || {};
      return { productId: details.productId ?? null, draftId: details.draftId ?? null };
    }
  }
  return null;
};

/**
 * `{ draftId, currentEditVersion }` when an API error is the server's 409
 * `DraftVersionConflict` (PR1-SCOPE.md §4 "Save, step by step" #4 — a
 * whole-document conflict, no field-level merge in PR 1), else null.
 */
export const readDraftVersionConflict = (error) => {
  for (const candidate of errorCandidates(error)) {
    if (candidate?.name === DRAFT_VERSION_CONFLICT_NAME) {
      const details = candidate?.details || {};
      return { draftId: details.draftId ?? null, currentEditVersion: details.currentEditVersion ?? null };
    }
  }
  return null;
};

/** `error.name` on the server's coded Create refusals (PR2-SCOPE.md §2, §3). */
export const CREATION_NOT_SUPPORTED_NAME = 'CreationNotSupported';
export const IDEMPOTENCY_KEY_REUSED_NAME = 'IdempotencyKeyReused';

/**
 * `{ reason }` when an API error is the server's 409 `CreationNotSupported`
 * (a source it can't copy yet), else null. The server no longer sends it for
 * a private price book — that copies with `view-prices` (else 403
 * `PriceAccessRequired`), a template's private cells cleared — but the
 * reader stays for any future refusal.
 * Same error-shape tolerance as the PR 1 readers.
 */
export const readCreationNotSupported = (error) => {
  for (const candidate of errorCandidates(error)) {
    if (candidate?.name === CREATION_NOT_SUPPORTED_NAME) {
      return { reason: candidate?.details?.reason ?? null };
    }
  }
  return null;
};

/**
 * `{ creationId }` when an API error is the server's 422
 * `IdempotencyKeyReused` — the key was already used with a DIFFERENT body
 * (a Create's `creationId`; null on a draft save), else null. It means the
 * caller reused a key across two different actions, not a retry.
 */
export const readIdempotencyKeyReused = (error) => {
  for (const candidate of errorCandidates(error)) {
    if (candidate?.name === IDEMPOTENCY_KEY_REUSED_NAME) {
      return { creationId: candidate?.details?.creationId ?? null };
    }
  }
  return null;
};

/** `error.name` on the server's coded "Build with AI" proposal refusals (PR 2b, spec §5). */
export const PROPOSAL_EXPIRED_NAME = 'ProposalExpired';
export const PROPOSAL_ALREADY_USED_NAME = 'ProposalAlreadyUsed';
export const PROPOSAL_NOT_READY_NAME = 'ProposalNotReady';
export const PROPOSALS_IN_PROGRESS_NAME = 'ProposalsInProgress';
export const PROPOSAL_FILE_TOO_LARGE_NAME = 'ProposalFileTooLarge';

/**
 * `{ expiresAt }` when an API error is the server's 409 `ProposalExpired`
 * (the proposal's create-window has passed), else null.
 */
export const readProposalExpired = (error) => {
  for (const candidate of errorCandidates(error)) {
    if (candidate?.name === PROPOSAL_EXPIRED_NAME) {
      return { expiresAt: candidate?.details?.expiresAt ?? null };
    }
  }
  return null;
};

/**
 * `{ draftId }` when an API error is the server's 409 `ProposalAlreadyUsed`
 * (a proposal is single-use for Create), else null.
 */
export const readProposalAlreadyUsed = (error) => {
  for (const candidate of errorCandidates(error)) {
    if (candidate?.name === PROPOSAL_ALREADY_USED_NAME) {
      return { draftId: candidate?.details?.draftId ?? null };
    }
  }
  return null;
};

/**
 * `{ status }` when an API error is the server's 409 `ProposalNotReady`
 * (Create was attempted before analysis finished), else null.
 */
export const readProposalNotReady = (error) => {
  for (const candidate of errorCandidates(error)) {
    if (candidate?.name === PROPOSAL_NOT_READY_NAME) {
      return { status: candidate?.details?.status ?? null };
    }
  }
  return null;
};

/**
 * `{ limit, active }` when an API error is the server's 409
 * `ProposalsInProgress` (too many active analyses at once for this company),
 * else null.
 */
export const readProposalsInProgress = (error) => {
  for (const candidate of errorCandidates(error)) {
    if (candidate?.name === PROPOSALS_IN_PROGRESS_NAME) {
      const details = candidate?.details || {};
      return { limit: details.limit ?? null, active: details.active ?? null };
    }
  }
  return null;
};

/**
 * `{ maxBytes }` when an API error is the server's 413 `ProposalFileTooLarge`,
 * else null.
 */
export const readProposalFileTooLarge = (error) => {
  for (const candidate of errorCandidates(error)) {
    if (candidate?.name === PROPOSAL_FILE_TOO_LARGE_NAME) {
      return { maxBytes: candidate?.details?.maxBytes ?? null };
    }
  }
  return null;
};

/**
 * `error.name` values in the product-AI spend guardrail family
 * (server/src/utils/product-ai/usage.js): `ProductAiLimitReached` (429,
 * `reason: 'monthly' | 'burst'`), `ProductAiNotAvailable` (403, not on the
 * company's plan), `ProductAiUsageUnavailable` (503, couldn't check the
 * allowance), `ProductAiPaused` (503, `reason: 'platform'`,
 * `retryAfterSeconds`). There is no `ProductAiRateLimited`.
 */
export const PRODUCT_AI_REFUSAL_NAMES = Object.freeze([
  'ProductAiLimitReached',
  'ProductAiNotAvailable',
  'ProductAiUsageUnavailable',
  'ProductAiPaused',
]);

/**
 * `{ name, message, reason, resetsAt, retryAfterSeconds }` when an API error
 * is one of the product-AI spend guardrail's coded refusals, else null.
 * `reason` and `resetsAt` come from `details` when present (`reason` is
 * `'monthly'` / `'burst'` on `ProductAiLimitReached`, `'platform'` on
 * `ProductAiPaused`; `ProductAiNotAvailable` / `ProductAiUsageUnavailable`
 * carry neither); `retryAfterSeconds` likewise (only `ProductAiPaused` and a
 * `burst` `ProductAiLimitReached` carry it), defaulting to null otherwise.
 */
export const readProductAiRefusal = (error) => {
  for (const candidate of errorCandidates(error)) {
    if (PRODUCT_AI_REFUSAL_NAMES.includes(candidate?.name)) {
      const details = candidate?.details || {};
      return {
        name: candidate.name,
        message: candidate?.message ?? null,
        reason: details.reason ?? null,
        resetsAt: details.resetsAt ?? null,
        retryAfterSeconds: details.retryAfterSeconds ?? null,
      };
    }
  }
  return null;
};

// ── PR 4b: pending edits to a shared attribute ─────────────────────────────
// `error.name` values from server services/shared-revisions.js. Same
// error-shape tolerance as the PR 1 readers; null on anything else.

const readNamed = (error, name, shape) => {
  for (const candidate of errorCandidates(error)) {
    if (candidate?.name === name) return shape(candidate?.details || {});
  }
  return null;
};
const list = (v) => (Array.isArray(v) ? v : []);

/** 409 `SharedRevisionRequired` — a breaking edit must go through a pending revision. */
export const readSharedRevisionRequired = (error) =>
  readNamed(error, 'SharedRevisionRequired', (d) => ({ attributeKey: d.attributeKey ?? null, reasons: list(d.reasons) }));

/** 409 `PendingRevisionStale` — the attribute changed under the pending edit. */
export const readPendingRevisionStale = (error) => readNamed(error, 'PendingRevisionStale', (d) => ({ conflicts: list(d.conflicts) }));

/** 409 `ConfirmationRequired` — Apply needs these reason codes confirmed. */
export const readConfirmationRequired = (error) => readNamed(error, 'ConfirmationRequired', (d) => ({ missing: list(d.missing) }));

/** 409 `PendingVersionConflict` — someone else saved the pending edit first. */
export const readPendingVersionConflict = (error) => readNamed(error, 'PendingVersionConflict', (d) => ({ version: d.version ?? null }));

/** `AttributeArchived` — the attribute is archived; restore it first. */
export const readAttributeArchived = (error) => readNamed(error, 'AttributeArchived', (d) => ({ attributeKey: d.attributeKey ?? null }));

/** `AttributeInUse` — products still use it. */
export const readAttributeInUse = (error) => readNamed(error, 'AttributeInUse', (d) => ({ productIds: list(d.productIds) }));

/** 503 `SharedLockUnavailable` — the server couldn't check who uses the attribute; try again. */
export const readSharedLockUnavailable = (error) => readNamed(error, 'SharedLockUnavailable', () => ({}));
